/**
 * Subclass Level-Up Bonuses Utility
 *
 * Calculates automatic stat bonuses and player choice requirements
 * for all subclasses that modify the level-up flow.
 *
 * @module subclassLevelUpBonuses
 */

import { hasSubclass } from './featureDetection';

/**
 * Get the tier name for a given level
 * @param {number} level - Character level (1-10)
 * @returns {string} Tier name ('foundation', 'specialization', 'mastery')
 */
const getTierForLevel = (level) => {
  if (level >= 1 && level <= 4) return 'foundation';
  if (level >= 5 && level <= 7) return 'specialization';
  if (level >= 8 && level <= 10) return 'mastery';
  return 'foundation';
};

/**
 * Check if character just acquired a specific tier
 * @param {number} level - New level
 * @param {string} tierToCheck - Tier to check ('foundation', 'specialization', 'mastery')
 * @returns {boolean}
 */
const justAcquiredTier = (level, tierToCheck) => {
  if (tierToCheck === 'foundation') return level === 1;
  if (tierToCheck === 'specialization') return level === 5;
  if (tierToCheck === 'mastery') return level === 8;
  return false;
};

/**
 * Get cumulative Stalwart threshold bonuses based on acquired features
 * @param {Object} character - Character object
 * @returns {number} Total threshold bonus (0, 1, 3, or 6)
 */
export const getStalwartThresholdBonus = (character) => {
  if (!hasSubclass(character, 'stalwart')) return 0;

  let bonus = 0;
  const features = character.subclassFeatures || {};

  // Foundation: Unwavering (+1 to all thresholds)
  if (features.foundation?.includes('Unwavering') || character.level >= 1) {
    bonus += 1;
  }

  // Specialization: Unrelenting (+2 more, cumulative = +3 total)
  if (features.specializations?.includes('Unrelenting') || character.level >= 5) {
    bonus += 2; // Cumulative: now +3 total
  }

  // Mastery: Undaunted (+3 more, cumulative = +6 total)
  if (features.masteries?.includes('Undaunted') || character.level >= 8) {
    bonus += 3; // Cumulative: now +6 total
  }

  return bonus;
};

/**
 * Get number of bonus domain cards for School of Knowledge wizard
 * @param {Object} character - Character object
 * @param {number} level - New level
 * @returns {number} Number of bonus domain cards (0, 1)
 */
export const getSchoolOfKnowledgeBonusCards = (character, level) => {
  if (!hasSubclass(character, 'school-of-knowledge')) return 0;

  if (level === 1) return 1; // Foundation: Prepared
  if (level === 5) return 1; // Specialization: Accomplished
  if (level === 9) return 1; // Mastery: Brilliant

  return 0;
};

/**
 * Get number of companion upgrades for Beastbound ranger
 * @param {number} level - New level
 * @returns {number} Number of companion upgrades to grant
 */
export const getBeastboundCompanionUpgrades = (level) => {
  let upgrades = 1; // Every level gets 1 upgrade

  if (level === 5) upgrades += 1; // Specialization: Expert Training (+1 extra)
  if (level === 9) upgrades += 2; // Mastery: Advanced Training (+2 extra)

  return upgrades;
};

/**
 * Get feature name for School of Knowledge bonus at specific level
 * @param {number} level - Level (1, 5, or 9)
 * @returns {string} Feature name
 */
export const getSchoolOfKnowledgeFeatureName = (level) => {
  if (level === 1) return 'Prepared (Foundation)';
  if (level === 5) return 'Accomplished (Specialization)';
  if (level === 9) return 'Brilliant (Mastery)';
  return '';
};

/**
 * Main function: Calculate all subclass bonuses for a level-up
 *
 * @param {Object} character - Character object
 * @param {number} newLevel - Level character is advancing to
 * @returns {Object} Bonus object with stats, thresholds, and extraChoices
 */
export const getSubclassLevelUpBonuses = (character, newLevel) => {
  const bonuses = {
    stats: {},              // { maxHitPoints: 1, maxStress: 1, evasion: 1 }
    thresholds: {},         // { minor: 1, major: 1, severe: 4 }
    extraChoices: []        // [{ type: 'domain_card', count: 1, ... }]
  };

  const subclass = character.subclass;
  const newTier = getTierForLevel(newLevel);

  // ==========================================
  // AUTO-APPLY STAT BONUSES
  // ==========================================

  // School of War (Wizard) - Foundation: Battlemage (+1 HP)
  if (hasSubclass(character, 'school-of-war') && justAcquiredTier(newLevel, 'foundation')) {
    bonuses.stats.maxHitPoints = 1;
  }

  // Vengeance (Guardian) - Foundation: At Ease (+1 Stress)
  if (hasSubclass(character, 'vengeance') && justAcquiredTier(newLevel, 'foundation')) {
    bonuses.stats.maxStress = 1;
  }

  // Nightwalker (Rogue) - Mastery: Fleeting Shadow (+1 Evasion)
  if (hasSubclass(character, 'nightwalker') && justAcquiredTier(newLevel, 'mastery')) {
    bonuses.stats.evasion = 1;
  }

  // ==========================================
  // AUTO-APPLY THRESHOLD BONUSES
  // ==========================================

  // Stalwart (Guardian) - Cumulative threshold bonuses
  if (hasSubclass(character, 'stalwart')) {
    const stalwartBonus = getStalwartThresholdBonus({ ...character, level: newLevel });

    if (stalwartBonus > 0) {
      bonuses.thresholds.minor = stalwartBonus;
      bonuses.thresholds.major = stalwartBonus;
      bonuses.thresholds.severe = stalwartBonus;
    }
  }

  // Winged Sentinel (Seraph) - Mastery: Ascendant (+4 Severe only)
  if (hasSubclass(character, 'winged-sentinel') && justAcquiredTier(newLevel, 'mastery')) {
    bonuses.thresholds.severe = 4;
  }

  // ==========================================
  // PLAYER CHOICE REQUIREMENTS
  // ==========================================

  // School of Knowledge (Wizard) - Bonus domain cards at L1/L5/L9
  if (hasSubclass(character, 'school-of-knowledge')) {
    const bonusCards = getSchoolOfKnowledgeBonusCards(character, newLevel);

    if (bonusCards > 0) {
      bonuses.extraChoices.push({
        type: 'domain_card',
        count: bonusCards,
        reason: `School of Knowledge: ${getSchoolOfKnowledgeFeatureName(newLevel)}`,
        maxLevel: newLevel,
        domains: character.domains || []
      });
    }
  }

  // Beastbound (Ranger) - Companion upgrades every level
  if (hasSubclass(character, 'beastbound')) {
    const companionUpgrades = getBeastboundCompanionUpgrades(newLevel);

    bonuses.extraChoices.push({
      type: 'companion_upgrade',
      count: companionUpgrades,
      reason: newLevel === 1 ? 'Beastbound Companion' :
              newLevel === 5 ? 'Expert Training (Specialization)' :
              newLevel === 9 ? 'Advanced Training (Mastery)' :
              'Companion Growth'
    });
  }

  return bonuses;
};

/**
 * Apply subclass stat bonuses to character
 *
 * @param {Object} character - Character object to update
 * @param {Object} bonuses - Bonuses from getSubclassLevelUpBonuses()
 * @returns {Object} Updated character object
 */
export const applySubclassStatBonuses = (character, bonuses) => {
  const updates = {};

  if (bonuses.stats.maxHitPoints) {
    updates.maxHitPoints = (character.maxHitPoints || 0) + bonuses.stats.maxHitPoints;
  }

  if (bonuses.stats.maxStress) {
    updates.maxStress = (character.maxStress || 0) + bonuses.stats.maxStress;
  }

  if (bonuses.stats.evasion) {
    updates.evasion = (character.evasion || 0) + bonuses.stats.evasion;
  }

  return { ...character, ...updates };
};

/**
 * Apply subclass threshold bonuses to character
 *
 * @param {Object} character - Character object to update
 * @param {Object} bonuses - Bonuses from getSubclassLevelUpBonuses()
 * @param {number} baseIncrease - Base threshold increase per level (usually +1)
 * @returns {Object} Updated damage thresholds
 */
export const applySubclassThresholdBonuses = (character, bonuses, baseIncrease = 1) => {
  const currentThresholds = character.damageThresholds || { minor: 0, major: 0, severe: 0 };

  return {
    minor: currentThresholds.minor + baseIncrease + (bonuses.thresholds.minor || 0),
    major: currentThresholds.major + baseIncrease + (bonuses.thresholds.major || 0),
    severe: currentThresholds.severe + baseIncrease + (bonuses.thresholds.severe || 0)
  };
};

export default {
  getSubclassLevelUpBonuses,
  getStalwartThresholdBonus,
  getSchoolOfKnowledgeBonusCards,
  getBeastboundCompanionUpgrades,
  getSchoolOfKnowledgeFeatureName,
  applySubclassStatBonuses,
  applySubclassThresholdBonuses
};
